/**
 * IndexedDB de ÁGORA — el único lugar donde vive lo que todavía no llegó al
 * servidor. Dos almacenes, dos momentos distintos del mismo peligro:
 *
 *   · `pendientes`  — grabaciones YA terminadas esperando entrar al acta
 *                     (ver `colaMedia.ts`).
 *   · `grabaciones` + `trozos` — grabaciones EN CURSO, escritas fragmento a
 *                     fragmento mientras se graba (ver `grabacionEnCurso.ts`).
 *                     Sin esto, una grabación de dos horas solo existe en la
 *                     memoria de la pestaña hasta que se detiene: si la laptop
 *                     se suspende o el navegador mata la pestaña, se pierde
 *                     entera.
 *
 * Una sola base y una sola versión para que el `onupgradeneeded` cree todo
 * junto: dos módulos abriendo la misma base con versiones distintas se
 * bloquean entre sí.
 */
const DB_NOMBRE = 'agora-media';
const DB_VERSION = 2;

export const ALMACEN_PENDIENTES = 'pendientes';
export const ALMACEN_GRABACIONES = 'grabaciones';
export const ALMACEN_TROZOS = 'trozos';

let dbPromesa: Promise<IDBDatabase> | null = null;

export function abrirDb(): Promise<IDBDatabase> {
  if (dbPromesa) return dbPromesa;
  dbPromesa = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('Este navegador no permite guardar la grabación.'));
      return;
    }
    const req = indexedDB.open(DB_NOMBRE, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(ALMACEN_PENDIENTES)) {
        db.createObjectStore(ALMACEN_PENDIENTES, { keyPath: 'idem' });
      }
      if (!db.objectStoreNames.contains(ALMACEN_GRABACIONES)) {
        db.createObjectStore(ALMACEN_GRABACIONES, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(ALMACEN_TROZOS)) {
        // Clave compuesta [grabacionId, indice]: los trozos de una grabación
        // quedan contiguos y en orden, y se leen por rango sin índice extra.
        db.createObjectStore(ALMACEN_TROZOS, { keyPath: ['grabacionId', 'indice'] });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB no disponible'));
  });
  return dbPromesa;
}

/** Una operación sobre un almacén, envuelta en su transacción. */
export function conAlmacen<T>(
  almacen: string,
  modo: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return abrirDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(almacen, modo);
        const req = fn(tx.objectStore(almacen));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}
